import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { HiX, HiLockClosed } from "react-icons/hi";
import { FaWallet, FaEthereum } from "react-icons/fa";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useAccount, useSignMessage } from "wagmi";

import useOutsideClick from "../../hooks/useOutsideClick";
import {
  selectGeneral,
  setShowWalletRequiredModal,
  setWalletJustLinked,
} from "../../redux/slices/generalSlice";
import { useLinkWalletMutation, useGetUserQuery } from "../../redux/slices/api/userApi";
import { VERIFICATION_MESSAGE } from "../../Constants";
import notify from "../common/notify";
import { Oval } from "react-loader-spinner";
import WalletDisplay from "../common/WalletDisplay";

/**
 * WalletRequiredModal Component
 * 
 * Shown when a social-auth user tries to perform a blockchain action
 * (funding a project, placing a bid, creating a fundraising project, etc.)
 * 
 * This modal guides users through connecting and linking their wallet
 * to their existing social-auth account.
 */
const WalletRequiredModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showWalletRequiredModal, walletRequiredAction, walletRequiredRedirect, isLoggedIn } = useSelector(selectGeneral);
  const modalRef = useRef(null);
  const { openConnectModal } = useConnectModal();
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const [linkWallet, { isLoading: isLinking }] = useLinkWalletMutation();
  const { refetch: refetchUser } = useGetUserQuery(undefined, { skip: !isLoggedIn });
  
  const [step, setStep] = useState(1); // 1: info, 2: connect, 3: sign, 4: success
  const [isSigningMessage, setIsSigningMessage] = useState(false);
  const [walletModalOpened, setWalletModalOpened] = useState(false);

  const handleClose = () => {
    dispatch(setShowWalletRequiredModal({ show: false }));
    setStep(1);
    setWalletModalOpened(false);
  };

  useOutsideClick(modalRef, handleClose, null, showWalletRequiredModal);

  // Handle wallet connection via RainbowKit
  const handleConnectWallet = async () => {
    if (openConnectModal) {
      setWalletModalOpened(true);
      openConnectModal();
    }
  };

  // Handle message signing and wallet linking
  const handleSignAndLink = async () => {
    console.log("🔐 Starting wallet linking process...");
    try {
      setIsSigningMessage(true);
      
      console.log("📝 Requesting signature for message:", VERIFICATION_MESSAGE);
      // Sign the verification message
      const signature = await signMessageAsync({
        message: VERIFICATION_MESSAGE,
      });

      console.log("✅ Signature received, linking wallet to account...");
      // Link wallet to account
      const result = await linkWallet({
        body: {
          public_address: address,
          signature,
        },
      }).unwrap();

      console.log("✅ Wallet linked successfully, result:", result);
      // Update local storage with new token that includes public_address
      if (result.token) {
        localStorage.setItem("token", result.token);
        // Also store the public address for quick reference
        localStorage.setItem("public_address", address.toLowerCase());
        console.log("💾 Token and address saved to localStorage");
      }

      setStep(4);
      notify("Wallet linked successfully!", "success");
      
      console.log("🔄 Refetching user data...");
      // Refetch user data to update the UI
      await refetchUser();
      
      // Set flag indicating wallet was just linked
      dispatch(setWalletJustLinked(true));
      
      console.log("⏱️ Closing modal and navigating to:", walletRequiredRedirect);
      // Close modal after short delay
      setTimeout(() => {
        handleClose();
        
        // Navigate to redirect URL if provided
        if (walletRequiredRedirect) {
          console.log("🔀 Navigating to:", walletRequiredRedirect);
          navigate(walletRequiredRedirect);
        } else {
          console.log("⚠️ No redirect URL provided");
        }
        
        // Clear the flag after a moment so components can react
        setTimeout(() => {
          dispatch(setWalletJustLinked(false));
        }, 100);
      }, 1000);
    } catch (error) {
      console.error("❌ Wallet linking error:", error);
      notify(error?.data?.message || "Failed to link wallet", "error");
    } finally {
      setIsSigningMessage(false);
    }
  };

  // Initialize step based on wallet connection status when modal opens
  useEffect(() => {
    if (showWalletRequiredModal) {
      if (isConnected && address) {
        // Wallet already connected - skip to sign step
        console.log("💡 Wallet already connected, skipping to step 3 (Sign & Link)");
        setStep(3);
      } else {
        // Wallet not connected - start from step 1
        console.log("💡 Wallet not connected, starting from step 1");
        setStep(1);
      }
    }
  }, [showWalletRequiredModal, isConnected, address]);

  // Watch for wallet connection and auto-advance
  useEffect(() => {
    if (isConnected && address) {
      if (step === 1 && walletModalOpened) {
        // User connected wallet from step 1, skip to step 3 (sign)
        console.log("💡 Wallet connected from step 1, moving to step 3");
        setStep(3);
        setWalletModalOpened(false);
      } else if (step === 2) {
        // User connected wallet from step 2, move to step 3
        console.log("💡 Wallet connected from step 2, moving to step 3");
        setStep(3);
        setWalletModalOpened(false);
      }
    }
  }, [isConnected, address, step, walletModalOpened]);

  // Reset step when modal closes
  useEffect(() => {
    if (!showWalletRequiredModal) {
      setStep(1);
      setWalletModalOpened(false);
    }
  }, [showWalletRequiredModal]);

  // Body overflow control
  useEffect(() => {
    if (showWalletRequiredModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showWalletRequiredModal]);

  if (!showWalletRequiredModal) return null;

  const actionMessages = {
    fund: "fund this project",
    bid: "place a bid",
    create_project: "create a fundraising project",
    receive_funds: "receive funds",
    default: "complete this action",
  };

  const actionMessage = actionMessages[walletRequiredAction] || actionMessages.default;

  return (
    <div className="min-w-[100vw] w-full h-screen flex justify-center items-center bg-secondary/80 backdrop-blur-md fixed top-0 left-0 z-50 p-4">
      <div
        ref={modalRef}
        className="w-full max-w-md flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl transform transition-all animate-fadeIn"
      >
        {/* Header */}
        <div className="relative w-full p-8 pb-4 text-center">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all duration-200"
          >
            <HiX className="text-gray-600" size={18} />
          </button>

          <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 items-center justify-center mb-4">
            {step === 4 ? (
              <FaEthereum className="text-primary" size={28} />
            ) : (
              <HiLockClosed className="text-amber-600" size={28} />
            )}
          </div>
          <h2 className="text-2xl font-bold text-secondary mb-2">
            {step === 4 ? "Wallet Connected!" : "Wallet Required"}
          </h2>
          <p className="text-gray-500 text-sm">
            {step === 4 
              ? "You can now perform blockchain transactions"
              : `To ${actionMessage}, you need to connect a wallet`
            }
          </p>
        </div>

        {/* Step Content */}
        <div className="w-full px-8 py-4">
          {step === 1 && (
            <div className="space-y-4">
              {/* Info about why wallet is needed */}
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                  <FaWallet className="text-amber-600" />
                  Why do I need a wallet?
                </h3>
                <ul className="text-sm text-amber-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-1">•</span>
                    <span>Securely send and receive cryptocurrency payments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-1">•</span>
                    <span>Participate in auctions and place bids on NFTs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-1">•</span>
                    <span>Create fundraising projects and receive funding</span>
                  </li>
                </ul>
              </div>

              {/* Supported wallets */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-600 mb-3">Supported wallets:</p>
                <div className="flex flex-wrap gap-2">
                  {["MetaMask", "WalletConnect", "Coinbase", "Trust Wallet"].map((wallet) => (
                    <span key={wallet} className="px-3 py-1 bg-white rounded-full text-xs text-gray-600 border border-gray-200">
                      {wallet}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="text-center py-6">
              <Oval
                height={48}
                width={48}
                color="#792CF3"
                secondaryColor="#e5e7eb"
                strokeWidth={4}
                wrapperClass="justify-center mb-4"
              />
              <p className="text-gray-600">Waiting for wallet connection...</p>
              <p className="text-sm text-gray-400 mt-2">
                Please connect your wallet in the popup
              </p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                <p className="text-green-700 font-medium mb-1">Wallet Connected!</p>
                <p className="text-sm text-green-600 font-mono">
                  <WalletDisplay address={address} />
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-600">
                  Now sign a message to verify ownership and link this wallet to your account.
                  This is a free signature and won't cost any gas.
                </p>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-gray-600">Redirecting...</p>
            </div>
          )}
        </div>

        {/* Actions */}
        {step !== 4 && (
          <div className="w-full px-8 pb-8">
            {step === 1 && (
              <button
                onClick={() => {
                  handleConnectWallet();
                }}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-primary via-[#8a5cff] to-[#7e7cff] hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] transition-all duration-300"
              >
                <FaWallet size={18} />
                <span>Connect Wallet</span>
              </button>
            )}

            {step === 2 && (
              <button
                onClick={handleConnectWallet}
                className="w-full py-3 px-6 rounded-xl font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all duration-200"
              >
                Open Wallet Selector Again
              </button>
            )}

            {step === 3 && (
              <button
                onClick={handleSignAndLink}
                disabled={isSigningMessage || isLinking}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-primary via-[#8a5cff] to-[#7e7cff] hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] transition-all duration-300 disabled:opacity-70 disabled:cursor-wait"
              >
                {isSigningMessage || isLinking ? (
                  <>
                    <Oval height={20} width={20} color="#fff" secondaryColor="#e5e7eb" strokeWidth={4} />
                    <span>Signing...</span>
                  </>
                ) : (
                  <>
                    <span>Sign & Link Wallet</span>
                  </>
                )}
              </button>
            )}

            <button
              onClick={handleClose}
              className="w-full py-3 px-6 rounded-xl font-medium text-gray-500 hover:text-gray-700 transition-all duration-200 mt-3"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletRequiredModal;
